import * as Team from "src/state/modules/team";
import * as Eden from "src/eden/content";
import { SaberGameSnapshot } from "src/utils/sabertooth-client";

// Normalized live scoreboard, consumed by both the in-browser overlay and the Cast receiver
// (which draws the same thing on the TV). Home/away ordering follows baseball convention so the
// board reads the way a stadium scoreboard does, regardless of which side our team is.
export interface Scoreboard {
  away: { name: string; score: number };
  home: { name: string; score: number };
  inning: number;
  half: "top" | "bottom";
  outs: number;
  // Which side is currently batting (away bats the top half, home the bottom).
  batting: "home" | "away";
}

// Builds the scoreboard from the replayed live game snapshot (scores/inning/outs — GameChanger's
// own book) plus the /live record (opponent name + which side we are). Returns null until both the
// replay and the live record are available.
export function computeScoreboard(
  state: SaberGameSnapshot | null,
  team: Team.Team | undefined,
  live: Eden.LiveGame | undefined
): Scoreboard | null {
  if (!state || !team || !live) return null;

  const ourId = team.id;
  // scores are keyed by team id; the other key is the opponent's (stream) team id.
  const oppId = Object.keys(state.scores).find((tid) => tid !== ourId);
  const ourSide = { name: team.name, score: state.scores[ourId] ?? 0 };
  const oppSide = { name: live.opponent_name ?? "Opponent", score: oppId ? state.scores[oppId] ?? 0 : 0 };

  // home_away tells us which side our team is; null (rare) falls back to treating us as away.
  const weAreHome = live.home_away === "home";

  return {
    home: weAreHome ? ourSide : oppSide,
    away: weAreHome ? oppSide : ourSide,
    inning: state.inning,
    half: state.half,
    outs: state.outs,
    // Canonical rule, independent of our home/away: top half = away at bat, bottom = home.
    batting: state.half === "top" ? "away" : "home",
  };
}
